// macadamia-app/src/components/pages/jurisprudences/JurisprudenceModalWrapper.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import JurisprudenceModal from '../../../macadamia-core/components/widgets/composite-text-widget/JurisprudenceModal';
import { apiClient } from '../../../utils/apiClient';

interface JurisprudenceData {
  reference: string;
  jurisdiction: string;
  chamber: string;
  number: string;
  ecli: string;
  formation: string;
  publication: string[];
  decisionDate: string;
  solution: string;
  summary: string;
  themes: string[];
  text: string;
  zones?: {
    introduction?: { start: number; end: number }[];
    expose?: { start: number; end: number }[];
    moyens?: { start: number; end: number }[];
    motivations?: { start: number; end: number }[];
    dispositif?: { start: number; end: number }[];
  };
  visa?: { title: string }[];
  contested?: {
    date: string;
    title: string;
    jurisdiction: string;
    chamber: string;
    solution: string;
    number: string;
  };
  rapprochements?: {
    title: string;
    number: string;
    description: string;
    theme: string[];
    url: string;
  }[];
}

interface JurisprudenceModalWrapperProps {
  jurisprudenceId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onFollowChange?: (id: string, isFollowed: boolean) => void;
  initialFollowState?: boolean;
}

const JurisprudenceModalWrapper: React.FC<JurisprudenceModalWrapperProps> = ({
  jurisprudenceId,
  isOpen,
  onClose,
  onFollowChange,
  initialFollowState = false
}) => {
  const { user } = useAuth();
  const [jurisprudenceData, setJurisprudenceData] = useState<JurisprudenceData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followState, setFollowState] = useState(initialFollowState);

  const fetchJurisprudence = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient(`/jurisprudences/decision?id=${id}&resolve_references=true`);

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des données de la décision');
      }

      const decisionData = await response.json();

      setJurisprudenceData({
        reference: id,
        jurisdiction: decisionData.jurisdiction || '',
        chamber: decisionData.chamber || '',
        number: decisionData.number || '',
        ecli: decisionData.ecli || '',
        formation: decisionData.formation || '',
        publication: decisionData.publication || [],
        decisionDate: decisionData.decision_date || '',
        solution: decisionData.solution || '',
        summary: decisionData.titlesAndSummaries?.[0]?.summary || '',
        themes: decisionData.themes || [],
        text: decisionData.text || '',
        zones: decisionData.zones || undefined,
        visa: decisionData.visa || [],
        rapprochements: decisionData.rapprochements || [],
        contested: decisionData.contested ? {
          date: decisionData.contested.date,
          title: decisionData.contested.title,
          jurisdiction: decisionData.contested.jurisdiction,
          chamber: decisionData.contested.chamber,
          solution: decisionData.contested.solution,
          number: decisionData.contested.number,
        } : undefined,
      });
    } catch (err) {
      console.error('Erreur lors du chargement de la jurisprudence:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && jurisprudenceId) {
      fetchJurisprudence(jurisprudenceId);
    }
  }, [isOpen, jurisprudenceId, fetchJurisprudence]);

  useEffect(() => {
    if (!isOpen) {
      setJurisprudenceData(null);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setFollowState(initialFollowState);
  }, [initialFollowState]);

  const handleFollowChange = useCallback((reference: string, isFollowed: boolean) => {
    setFollowState(isFollowed);
    onFollowChange?.(reference, isFollowed);
  }, [onFollowChange]);

  if (!isOpen) return null;

  return (
    <JurisprudenceModal
      isVisible={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      error={error}
      reference={jurisprudenceData?.reference || jurisprudenceId || ''}
      jurisdiction={jurisprudenceData?.jurisdiction || ''}
      chamber={jurisprudenceData?.chamber || ''}
      number={jurisprudenceData?.number || ''}
      ecli={jurisprudenceData?.ecli || ''}
      formation={jurisprudenceData?.formation || ''}
      publication={jurisprudenceData?.publication || []}
      decisionDate={jurisprudenceData?.decisionDate || ''}
      solution={jurisprudenceData?.solution || ''}
      summary={jurisprudenceData?.summary || ''}
      themes={jurisprudenceData?.themes || []}
      text={jurisprudenceData?.text || ''}
      zones={jurisprudenceData?.zones}
      visa={jurisprudenceData?.visa || []}
      contested={jurisprudenceData?.contested}
      rapprochements={jurisprudenceData?.rapprochements || []}
      userRole={user?.role || null}
      jurisprudenceFollowState={followState}
      onJurisprudenceFollowChange={handleFollowChange}
    />
  );
};

export default JurisprudenceModalWrapper;
